import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BellIcon,
  CheckCheckIcon,
  RefreshCwIcon,
  InfoIcon,
  CheckCircle2Icon,
  AlertTriangleIcon,
  XCircleIcon,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../utils/cn';
import { useNotificationStore, type Notification } from '../../stores/notificationStore';
import { getNotifications, markAllRead } from '../../api/notifications';
import { QUERY_KEYS } from '../../constants/queryKeys';
import { formatRelativeTime } from '../../utils/formatters';

function NotificationIcon({ type }: { type: Notification['type'] }) {
  switch (type) {
    case 'success':
      return <CheckCircle2Icon className="h-4 w-4 text-green-500" />;
    case 'warning':
      return <AlertTriangleIcon className="h-4 w-4 text-amber-500" />;
    case 'error':
      return <XCircleIcon className="h-4 w-4 text-red-500" />;
    default:
      return <InfoIcon className="h-4 w-4 text-blue-500" />;
  }
}

export default function NotificationDrawer() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isDrawerOpen = useNotificationStore((s) => s.isDrawerOpen);
  const setDrawerOpen = useNotificationStore((s) => s.setDrawerOpen);
  const notifications = useNotificationStore((s) => s.notifications);
  const setNotifications = useNotificationStore((s) => s.setNotifications);

  const { data, refetch, isLoading } = useQuery({
    queryKey: QUERY_KEYS.notifications.list(),
    queryFn: getNotifications,
    staleTime: Infinity,           // no re-fetch on window focus (polling handles freshness)
    refetchInterval: 30_000,       // poll every 30 s to keep badge count accurate
    refetchIntervalInBackground: false, // pause when the tab is inactive
  });

  // Sync fetched data into store
  useEffect(() => {
    if (data) setNotifications(data);
  }, [data, setNotifications]);

  const markAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.list() });
    },
  });

  function handleNotificationClick(notification: Notification) {
    setDrawerOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  }

  return (
    <Sheet open={isDrawerOpen} onOpenChange={(o) => !o && setDrawerOpen(false)}>
      <SheetContent
        side="right"
        className="flex flex-col w-[380px] p-0"
      >
        {/* Header */}
        <SheetHeader className="flex flex-row items-center justify-between px-4 py-3 border-b">
          <SheetTitle className="text-base font-semibold">Notifications</SheetTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => refetch()}
              aria-label="Refresh notifications"
              disabled={isLoading}
            >
              <RefreshCwIcon className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => markAllMutation.mutate()}
              aria-label="Mark all read"
              disabled={markAllMutation.isPending}
            >
              <CheckCheckIcon className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Notification list */}
        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="flex flex-col gap-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-md bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-400">
              <BellIcon className="h-8 w-8" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <ul className="divide-y">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50',
                      !n.is_read && 'bg-blue-50/50 hover:bg-blue-50',
                    )}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="mt-0.5 shrink-0">
                      <NotificationIcon type={n.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm', !n.is_read && 'font-medium')}>{n.title}</p>
                      {n.body && (
                        <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{n.body}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-400">
                        {formatRelativeTime(n.created_at)}
                      </p>
                    </div>
                    {!n.is_read && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
